/**
 * @file Helper for surface settings
 */
import { __ } from '@@/bits/intl'
import { GroupByTypes, ReactionType, SortByTypes } from '@padlet/arvo'
import { processedUrl } from '@padlet/vivaldi-client'

const DEFAULT_MAX_GRADE = 100

const GROUP_BY_DATA_OLD = {
  [GroupByTypes.None]: {
    icon: 'close',
    name: __('No groups'),
    description: '',
  },
  [GroupByTypes.Section]: {
    icon: 'section',
    name: __('Sections'),
    description: __('Group posts by their assigned section'),
  },
}

const GROUP_BY_DATA = {
  [GroupByTypes.None]: {
    icon: 'close_small_thin',
    name: __('No groups'),
    description: '',
  },
  [GroupByTypes.Section]: {
    icon: 'section_thin',
    name: __('Sections'),
    description: __('Group posts by their assigned section'),
  },
  [GroupByTypes.Author]: {
    icon: 'edit_account_outline',
    name: __('Author'),
    description: __('Group posts by who wrote them'),
  },
  [GroupByTypes.Color]: {
    icon: 'wallpaper_type_gradient',
    name: __('Color'),
    description: __('Group posts by their assigned color'),
  },
}

const REACTIONS_DATA = {
  [ReactionType.None]: {
    icon: 'border',
    name: __('None'),
    description: __('No reactions'),
    previewIcon: 'border',
  },
  [ReactionType.Like]: {
    icon: 'heart_outline_thin',
    name: __('Like'),
    description: __('Like posts'),
    previewIcon: 'heart_filled',
  },
  [ReactionType.Vote]: {
    icon: 'thumb_up_outline',
    name: __('Vote'),
    description: __('Upvote or downvote posts'),
    previewIcon: 'thumb_up_filled',
  },
  [ReactionType.Star]: {
    icon: 'star_outline',
    name: __('Star'),
    description: __('Give posts 1-5 stars'),
    previewIcon: 'star_filled',
  },
  [ReactionType.Grade]: {
    icon: 'checkbox_person_outline',
    name: __('Grade'),
    description: __('Give numeric scores to posts'),
    previewIcon: 'checkbox_person_filled',
  },
}

function processedEmojiUrl(url: string): string {
  return processedUrl(url, { width: 64, height: 64, preset: 'icon' })
}

export type SortOrder = 'asc' | 'desc'

type GetSortByTypePrefix<T> = T extends `${infer U}_${'first' | 'last' | 'asc' | 'desc'}` ? U : T

type SortByAttribute = GetSortByTypePrefix<`${SortByTypes}`>

const getSortByArrayFromAttribute = (attribute: SortByAttribute): SortByTypes[] => {
  switch (attribute) {
    case 'manual_new_posts':
      return ManualSortByArray
    case 'reactions':
      return ReactionSortByArray
    case 'date_published':
      return DatePublishedSortByArray
    case 'post_subject':
      return [SortByTypes.PostSubjectAsc, SortByTypes.PostSubjectDesc]
    case 'shuffle':
      return [SortByTypes.Shuffle]
    default:
      return []
  }
}

const isSortingBy = (wishSortBy: SortByTypes, attribute: SortByAttribute): boolean => {
  return getSortByArrayFromAttribute(attribute).includes(wishSortBy)
}

type ManualSortBy = SortByTypes.ManualNewPostsFirst | SortByTypes.ManualNewPostsLast
const ManualSortByArray: ManualSortBy[] = [SortByTypes.ManualNewPostsFirst, SortByTypes.ManualNewPostsLast]

const DatePublishedSortByArray = [SortByTypes.DatePublishedSortAsc, SortByTypes.DatePublishedSortDesc]
type DatePublishedSortBy = typeof DatePublishedSortByArray[number]

type ReactionSortBy = SortByTypes.ReactionsAsc | SortByTypes.ReactionsDesc
const ReactionSortByArray: ReactionSortBy[] = [SortByTypes.ReactionsAsc, SortByTypes.ReactionsDesc]

const ReactionSortByAscKeyArray = ['likes_lowest', 'grades_lowest', 'stars_lowest', 'votes_lowest'] as const

type ReactionSortByAscKey = typeof ReactionSortByAscKeyArray[number]

const ReactionSortByDescKeyArray = ['likes_highest', 'grades_highest', 'stars_highest', 'votes_highest'] as const

type ReactionSortByDescKey = typeof ReactionSortByDescKeyArray[number]

type PostSubjectSortBy = SortByTypes.PostSubjectAsc | SortByTypes.PostSubjectDesc

const REACTION_SORT_BY_DATA: Record<
  Exclude<ReactionType, 'none'>,
  Array<{ key: ReactionSortByAscKey | ReactionSortByDescKey; icon: string; title: string }>
> = {
  [ReactionType.Like]: [
    {
      key: 'likes_highest',
      icon: 'heart_arrow_down',
      title: __('Most to least'),
    },
    {
      key: 'likes_lowest',
      icon: 'heart_arrow_up',
      title: __('Least to most'),
    },
  ],
  [ReactionType.Grade]: [
    {
      key: 'grades_highest',
      icon: 'checkbox_arrow_down',
      title: __('Highest to lowest'),
    },
    {
      key: 'grades_lowest',
      icon: 'checkbox_arrow_up',
      title: __('Lowest to highest'),
    },
  ],
  [ReactionType.Star]: [
    {
      key: 'stars_highest',
      icon: 'star_arrow_down',
      title: __('Highest to lowest'),
    },
    {
      key: 'stars_lowest',
      icon: 'star_arrow_up',
      title: __('Lowest to highest'),
    },
  ],
  [ReactionType.Vote]: [
    {
      key: 'votes_highest',
      icon: 'thumb_arrow_down',
      title: __('Most to least'),
    },
    {
      key: 'votes_lowest',
      icon: 'thumb_arrow_up',
      title: __('Least to most'),
    },
  ],
}

const MANUAL_SORT_BY_DATA: Record<ManualSortBy, { key: ManualSortBy; title: string; icon: string }> = {
  manual_new_posts_first: {
    key: SortByTypes.ManualNewPostsFirst,
    title: __('New posts added first'),
    icon: 'create_to_front',
  },
  manual_new_posts_last: {
    key: SortByTypes.ManualNewPostsLast,
    title: __('New posts added last'),
    icon: 'create_to_back',
  },
}

const DATE_PUBLISHED_SORT_BY_DATA = {
  [SortByTypes.DatePublishedSortDesc]: {
    key: SortByTypes.DatePublishedSortDesc,
    title: __('Newest to oldest'),
    icon: 'clock_arrow_down',
  },
  [SortByTypes.DatePublishedSortAsc]: {
    key: SortByTypes.DatePublishedSortAsc,
    title: __('Oldest to newest'),
    icon: 'clock_arrow_up',
  },
}

const POST_SUBJECT_SORT_BY_DATA = {
  [SortByTypes.PostSubjectAsc]: {
    key: SortByTypes.PostSubjectAsc,
    title: __('Alphabetical order'),
    icon: 'sort_name_outline',
  },
  [SortByTypes.PostSubjectDesc]: {
    key: SortByTypes.PostSubjectDesc,
    title: __('Reverse alphabetical order'),
    icon: 'sort_name_outline_reverse',
  },
}

const POST_SHUFFLE_SORT_BY_DATA = {
  [SortByTypes.Shuffle]: {
    key: SortByTypes.Shuffle,
    title: __('Posts appear randomly'),
    icon: 'arrow_2_rectangular_clockwise_thin',
  },
}

const ReactionSortToKeyMap: Record<
  ReactionSortBy,
  Record<Exclude<ReactionType, 'none'>, ReactionSortByAscKey | ReactionSortByDescKey>
> = {
  reactions_asc: {
    [ReactionType.Like]: 'likes_lowest',
    [ReactionType.Grade]: 'grades_lowest',
    [ReactionType.Star]: 'stars_lowest',
    [ReactionType.Vote]: 'votes_lowest',
  },
  reactions_desc: {
    [ReactionType.Like]: 'likes_highest',
    [ReactionType.Grade]: 'grades_highest',
    [ReactionType.Star]: 'stars_highest',
    [ReactionType.Vote]: 'votes_highest',
  },
}

export {
  DATE_PUBLISHED_SORT_BY_DATA,
  DEFAULT_MAX_GRADE,
  DatePublishedSortBy,
  DatePublishedSortByArray,
  GROUP_BY_DATA,
  GROUP_BY_DATA_OLD,
  MANUAL_SORT_BY_DATA,
  ManualSortBy,
  ManualSortByArray,
  POST_SHUFFLE_SORT_BY_DATA,
  POST_SUBJECT_SORT_BY_DATA,
  PostSubjectSortBy,
  REACTIONS_DATA,
  REACTION_SORT_BY_DATA,
  ReactionSortBy,
  ReactionSortByArray,
  ReactionSortByAscKey,
  ReactionSortByAscKeyArray,
  ReactionSortByDescKey,
  ReactionSortByDescKeyArray,
  ReactionSortToKeyMap,
  ReactionType,
  SortByTypes,
  isSortingBy,
  processedEmojiUrl,
}
